'use client';

import { useEffect, useRef, useState } from 'react';

import ErrorBoundary from '@/components/ErrorBoundary';

interface Tier {
  id: string;
  name: string;
  price: string;
  currency: string;
  tagline: string;
  color: string;
  colorBg: string;
  colorBorder: string;
  featured: boolean;
  perks: string[];
  ctaLabel: string;
  ctaClass: string;
  armorIcon: string;
  discordRoleId: string;
}

const TIERS: Tier[] = [
  {
    id: 'burlap',
    name: 'Burlap',
    price: '2.99',
    currency: 'eur',
    tagline: 'Protection? Minimal at best. Queue? Minimal waiting.',
    color: '#a3a380',
    colorBg: 'rgba(163, 163, 128, 0.08)',
    colorBorder: 'rgba(163, 163, 128, 0.2)',
    featured: false,
    perks: [
      'Queue Priority',
      'Custom Chat Color',
      'Burlap Discord Role',
    ],
    ctaLabel: 'Get Burlap',
    ctaClass: 'bg-[rgba(163,163,128,0.15)] hover:bg-[rgba(163,163,128,0.3)] border border-[rgba(163,163,128,0.3)] text-[#c8c8a0]',
    armorIcon: '🪨',
    discordRoleId: 'burlap',
  },
  {
    id: 'roadsign',
    name: 'Roadsign',
    price: '5.99',
    currency: 'eur',
    tagline: 'Strong enough to get by. Exclusive features for those who want more.',
    color: '#60a5fa',
    colorBg: 'rgba(96, 165, 250, 0.08)',
    colorBorder: 'rgba(96, 165, 250, 0.3)',
    featured: true,
    perks: [
      'Skip Queue Instantly',
      'Custom Chat Color + Tag',
      'Roadsign Discord Role',
      'Exclusive Giveaways',
    ],
    ctaLabel: 'Get Roadsign',
    ctaClass: 'bg-[rgba(96,165,250,0.15)] hover:bg-[rgba(96,165,250,0.3)] border border-[rgba(96,165,250,0.3)] text-[#93c5fd]',
    armorIcon: '🛡️',
    discordRoleId: 'roadsign',
  },
  {
    id: 'fullmetal',
    name: 'Full Metal',
    price: '9.99',
    currency: 'eur',
    tagline: 'Unstoppable. Reserved slots, animations, and raffles — everything a Rust Lord needs.',
    color: '#ef4444',
    colorBg: 'rgba(239, 68, 68, 0.08)',
    colorBorder: 'rgba(239, 68, 68, 0.25)',
    featured: false,
    perks: [
      'Instant Queue Skip',
      'Animated Chat Tag',
      'Full Metal Discord Role',
      'Monthly Skin Raffles',
      'Reserved Slot',
    ],
    ctaLabel: 'Get Full Metal',
    ctaClass: 'bg-[rgba(239,68,68,0.15)] hover:bg-[rgba(239,68,68,0.3)] border border-[rgba(239,68,68,0.3)] text-[#fca5a5]',
    armorIcon: '⚔️',
    discordRoleId: 'fullmetal',
  },
];

function TierCard({ tier, index }: { tier: Tier; index: number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tierId: tier.id,
          tierName: tier.name,
          price: tier.price,
          currency: tier.currency,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to start checkout. Please try again.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`tier-card-diagonal flex flex-col ${
        tier.id === 'fullmetal' ? 'tier-fullmetal' : tier.id === 'roadsign' ? 'tier-roadsign' : 'tier-burlap'
      } ${tier.featured ? 'featured' : ''}`}
      style={{
        transitionDelay: `${index * 0.1}s`,
        background: tier.colorBg,
        borderColor: tier.colorBorder,
      }}
      role="article"
      aria-label={`${tier.name} supporter tier`}
    >
      {/* Diagonal accent border */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${tier.color}40 0%, transparent 70%)`,
          clipPath: 'polygon(100% 0, 100% 100%, 0 0)'
        }}
        aria-hidden="true"
      />
      
      {/* Featured badge - Top Right */}
      {tier.featured && tier.id === 'roadsign' && (
        <div className="absolute top-4 right-4 z-10">
          <span
            className="px-3 py-1.5 rounded-lg text-xs font-800 text-white"
            style={{
              background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
              fontWeight: 800,
              boxShadow: '0 0 25px rgba(96,165,250,0.5)',
              border: '2px solid rgba(96,165,250,0.6)'
            }}
          >
            Popular
          </span>
        </div>
      )}
      {tier.id === 'fullmetal' && (
        <div className="absolute top-4 right-4 z-10">
          <span
            className="px-3 py-1.5 rounded-lg text-xs font-800 text-white"
            style={{
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              fontWeight: 800,
              boxShadow: '0 0 25px rgba(239,68,68,0.5)',
              border: '2px solid rgba(239,68,68,0.6)'
            }}
          >
            Ultimate
          </span>
        </div>
      )}

      <div className="p-9 flex flex-col h-full relative z-10">
        {/* Header */}
        <div className="mb-7">
          <div className="flex items-center gap-4 mb-5">
            <span className="text-4xl" aria-hidden="true">{tier.armorIcon}</span>
            <h3
              className="font-heading text-3xl font-900"
              style={{ fontWeight: 900, color: tier.color }}
            >
              {tier.name}
            </h3>
          </div>
          <p className="text-base text-vanta-muted leading-relaxed">{tier.tagline}</p>
        </div>

        {/* Price */}
        <div className="mb-9 pb-9 border-b-2" style={{ borderColor: tier.colorBorder }}>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-heading font-700 text-vanta-muted" style={{ fontWeight: 700 }}>
              €
            </span>
            <span
              className="font-heading font-900 leading-none"
              style={{ fontSize: '4rem', fontWeight: 900, color: tier.color }}
            >
              {tier.price}
            </span>
            <span className="text-vanta-dim text-base mb-3">/mo</span>
          </div>
        </div>

        {/* Perks */}
        <div className="flex-1 mb-9">
          <ul className="space-y-4" role="list" aria-label={`${tier.name} perks`}>
            {tier.perks.map((perk) => (
              <li key={perk} className="flex items-center gap-4 text-base text-vanta-text">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={tier.color}
                  strokeWidth="3"
                  aria-hidden="true"
                >
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {perk}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <button
          onClick={handlePurchase}
          disabled={loading}
          className={`w-full py-5 rounded-2xl text-base font-800 transition-all duration-300 ${tier.ctaClass} ${
            loading ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105 hover:shadow-2xl'
          }`}
          style={{ fontWeight: 800 }}
          aria-label={`Purchase ${tier.name} for ${tier.currency}${tier.price} per month`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
              </svg>
              Processing...
            </span>
          ) : (
            tier.ctaLabel
          )}
        </button>
        {error && (
          <p className="mt-2 text-xs text-red-400 text-center" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function PricingCards() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Simulate initial load check
      if (TIERS && TIERS.length > 0) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error loading pricing tiers:', err);
      setError('Failed to load pricing tiers');
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-vanta-muted">Loading pricing tiers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {TIERS?.map((tier, i) => (
        <TierCard key={tier.id} tier={tier} index={i} />
      ))}
    </div>
  );
}

export default function SupporterPerks() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('reveal-visible');
        });
      },
      { threshold: 0.08 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="supporter-perks"
      ref={sectionRef}
      className="relative py-32 px-6"
      aria-labelledby="perks-heading"
    >
      {/* BG treatment */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(106,92,255,0.04) 40%, rgba(106,92,255,0.04) 60%, transparent 100%)',
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-6 reveal-hidden">
          <div className="section-label mb-4">Support VANTA</div>
          <h2
            id="supporter-heading"
            className="font-heading text-4xl md:text-5xl font-800 text-white mb-4"
            style={{ fontWeight: 800 }}
          >
            Supporter Perks
          </h2>
          <p className="text-vanta-muted max-w-xl mx-auto leading-relaxed">
            Help keep VANTA running and get exclusive cosmetic perks. All perks are visual only.
          </p>
        </div>

        {/* Never P2W badge */}
        <div className="flex justify-center mb-8 reveal-hidden">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-accent">
            <svg className="w-4 h-4 text-vanta-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-sm font-600 text-vanta-accent-light" style={{ fontWeight: 600 }}>
              Never Pay-to-Win • Cosmetic Only
            </span>
          </div>
        </div>

        {/* Get Started CTA */}
        <div className="flex justify-center mb-16 reveal-hidden">
          <a
            href="#supporter"
            className="px-8 py-3 rounded-xl bg-vanta-accent hover:bg-vanta-accent-hover text-white font-700 transition-all duration-300 hover:scale-105"
            style={{ fontWeight: 700 }}
          >
            Get Started
          </a>
        </div>

        {/* Pricing Cards */}
        <ErrorBoundary componentName="Pricing Cards">
          <PricingCards />
        </ErrorBoundary>

        {/* Bottom note */}
        <p className="text-center text-xs text-vanta-dim mt-10 reveal-hidden">
          All payments processed securely via Stripe. Cancel anytime. Perks activate instantly.
        </p>
      </div>
    </section>
  );
}

// Add global styles for ripple effect
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .ripple-effect {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);
      transform: scale(0);
      animation: ripple-animation 0.6s ease-out;
      pointer-events: none;
    }
    
    @keyframes ripple-animation {
      to {
        transform: scale(2);
        opacity: 0;
      }
    }
  `;
  if (!document.querySelector('style[data-ripple]')) {
    style.setAttribute('data-ripple', 'true');
    document.head.appendChild(style);
  }
}